/**
 * WinBizGrowth API client.
 *
 * Base URL comes from REACT_APP_BACKEND_URL (E-3 convention). Never hardcode.
 * Every backend route lives under `${REACT_APP_BACKEND_URL}/api/*`.
 *
 * The JWT is stored in localStorage under key `wc_token` and attached as
 * `Authorization: Bearer <token>` unless the caller passes `{ auth: false }`.
 *
 * 401 responses clear the token and dispatch a `wc:auth-expired` window
 * event so the AuthProvider can boot the user back to /login without every
 * caller having to catch the error.
 */

const RAW_BACKEND = (
  process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/+$/, "");
export const API_BASE = RAW_BACKEND ? `${RAW_BACKEND}/api` : "/api";

export const TOKEN_KEY = "wc_token";

export function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    /* localStorage disabled — ignore */
  }
}

export function clearToken() {
  setToken(null);
}

export class ApiError extends Error {
  constructor({ status, detail, code, body }) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.code = code;
    this.body = body;
  }
}

async function parseResponseBody(res) {
  const ctype = res.headers.get("content-type") || "";
  if (ctype.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

/**
 * Fetch wrapper.
 * @param {string} path — starts with `/`, e.g. `/auth/login`.
 * @param {object} [opts]
 * @param {"GET"|"POST"|"PATCH"|"PUT"|"DELETE"} [opts.method="GET"]
 * @param {object} [opts.body]  — JSON body (auto-stringified)
 * @param {boolean} [opts.auth=true]
 * @param {AbortSignal} [opts.signal]
 * @param {object} [opts.headers]
 */
export async function api(path, opts = {}) {
  const {
    method = "GET",
    body,
    auth = true,
    signal,
    headers = {},
  } = opts;

  const finalHeaders = {
    Accept: "application/json",
    ...headers,
  };
  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getToken();
    if (token) finalHeaders["Authorization"] = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: finalHeaders,
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
          ? body
          : JSON.stringify(body),
      signal,
      // Include cookies for any future cookie-based flows; harmless on
      // Bearer-based requests.
      credentials: "include",
    });
  } catch (netErr) {
    throw new ApiError({
      status: 0,
      detail: "Network error — check your connection and try again.",
      body: null,
    });
  }

  const payload = await parseResponseBody(res);

  if (res.ok) {
    return payload;
  }

  // Auth expired → clear token + broadcast so the AuthProvider can react.
  if (res.status === 401 && auth) {
    clearToken();
    try {
      window.dispatchEvent(new CustomEvent("wc:auth-expired"));
    } catch {
      /* SSR-safe fallback */
    }
  }

  // FAT #19 Phase 3 — Global 402 backstop. Any capability-gate failure that
  // slips past a <CapabilityGate> wrapper routes to /upgrade silently. The
  // <SubscriptionProvider> mounted in AppRouter listens and pushes the route.
  if (res.status === 402 && payload && payload.detail && typeof payload.detail === "object") {
    try {
      window.dispatchEvent(new CustomEvent("wc:capability-gate", {
        detail: {
          code: payload.detail.code,
          capability: payload.detail.capability,
          plan: payload.detail.plan,
          upgrade_url: payload.detail.upgrade_url || "/upgrade",
        },
      }));
    } catch {
      /* SSR-safe fallback */
    }
  }

  const detail =
    (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
    (typeof payload === "string" ? payload : null) ||
    `HTTP ${res.status}`;

  throw new ApiError({
    status: res.status,
    detail,
    code: payload && payload.code,
    body: payload,
  });
}

// Sugar helpers.
export const apiGet = (path, opts) => api(path, { ...opts, method: "GET" });
export const apiPost = (path, body, opts) =>
  api(path, { ...opts, method: "POST", body });
export const apiPatch = (path, body, opts) =>
  api(path, { ...opts, method: "PATCH", body });
export const apiPut = (path, body, opts) =>
  api(path, { ...opts, method: "PUT", body });
export const apiDelete = (path, opts) =>
  api(path, { ...opts, method: "DELETE" });


/**
 * Fetch a file from an authenticated backend endpoint and trigger a
 * browser download. Uses fetch(Bearer) + blob + <a download> because
 * `window.open()` on an auth-protected endpoint yields 401 (no Bearer
 * header can be attached to a top-level navigation).
 */
export async function downloadAuthedFile(path, filename) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new ApiError({ status: res.status, detail: `HTTP ${res.status}`, body: null });
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  }
}
